import { ChangeEvent } from 'react';
import { Accordion, Stack } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { SFCAccordion } from '../../../../../../components/accordion/SFCAccordion';
import { SimpleButton } from '../../../../../../components/buttons/SimpleButton';
import { DetailSection } from '../../../../../../components/generic';
import { SimpleTable } from '../../../../../../components/tables/SimpleTable';
import { getCurrentUser } from '../../../../../../services/helper';
import { Course } from '../../../../../../types/course.type';
import { Lesson } from '../../../../../../types/lesson.type';
import {
    revisionsColumns,
    sessionExpensesColumns
} from '../../../../../../utils/consts/columnDefs/sessionColumns';
import SafeHTMLDisplay from '../../../../../common/SafeHTMLDisplay';
import { SessionContactsSection } from './components/SessionContactsSection';
import { SessionInvoicesSection } from './components/SessionInvoicesSection';
import { SessionNotesSection } from './components/SessionNotesSection';
import { SessionParentEmailsSection } from './components/SessionParentEmailsSection';
import { SessionReceiptsSection } from './components/SessionReceiptsSection';
import { SessionResourcesSection } from './components/SessionResourcesSection';
import { SessionClassSchedule } from './SessionClassSchedule';
import { lessonAssignmentActionFn } from './SessionDetail.types';
import { SessionRosterOrEnrollment, SessionWaitlistOrWaivers } from './SessionEnrollmentSection';

const currentUser = getCurrentUser();

interface Props {
    course: Course | undefined;
    sortedLessons: Lesson[];
    featuredTlps: any[];
    actionCallback: lessonAssignmentActionFn;
    onWelcomeEmailClick: (email: string) => void;
    onPDFClick: (mode: string) => void;
    onSendTextReminder: (type: string) => void;
    rosterEmails: string;
    waitlistEmails: string;
    isUpdatingEmails: boolean;
    onRosterEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onUpdateEmails: () => void;
}

export const SessionDetailAccordion = ({
                                           course,
                                           sortedLessons,
                                           featuredTlps,
                                           actionCallback,
                                           onWelcomeEmailClick,
                                           onPDFClick,
                                           onSendTextReminder,
                                           rosterEmails,
                                           waitlistEmails,
                                           isUpdatingEmails,
                                           onRosterEmailChange,
                                           onUpdateEmails
                                       }: Props) => {
    const navigate = useNavigate();

    const isInstructor = !!currentUser?.isInstructor;
    const isWebsite = course?.payment_type === 'website';

    const rosterHeader = isWebsite ? `Roster (${course?.enrollment})` : 'Enrollment Tool';
    const waitlistHeader = isWebsite ? `Waitlist (${course?.children?.length})` : 'Waivers';

    // Per Emy UAT instructor rules:
    //  - main column for instructors = Class Schedule, Instructor Notes, Roster (in that order)
    //  - hide Waitlist, ASRE Tool, and Enrollment Tool sections
    // The Roster/Enrollment item (eventKey 2) is one merged component: it renders as
    // "Roster" on website sessions and "Enrollment Tool" on onsite sessions. So instructors
    // may see it ONLY on website sessions (Roster); on onsite it would be the Enrollment Tool,
    // which is hidden. Waitlist/Waivers (eventKey 3) is never shown to instructors.
    const showRosterOrEnrollment = isInstructor ? isWebsite : true;
    const showWaitlistOrWaivers = !isInstructor;
    const showExpenses = !isInstructor;
    const showParentEmails = !isInstructor;
    const showRevisions = !isInstructor;

    // Central column order (Sam V2): Class Schedule, Instructor Notes, Enrollment/Roster,
    // Waitlist/Waivers, Expenses, Parent Emails, Revisions.
    // EventKeys retained from prior implementation for stable accordion state.
    return (
        <SFCAccordion defaultActiveKey={['0', '1', '2', '3', '4', '6']}>
            {/* Both: Class Schedule (Sam: first in central column) */}
            <Accordion.Item eventKey="4">
                <Accordion.Header>Class Schedule</Accordion.Header>
                <Accordion.Body>
                    <SessionClassSchedule
                        course={course}
                        sortedLessons={sortedLessons}
                        featuredTlps={featuredTlps}
                        actionCallback={actionCallback}
                    />
                </Accordion.Body>
            </Accordion.Item>

            {/* Both: Instructor Notes */}
            <Accordion.Item eventKey="1">
                <Accordion.Header>Instructor Notes</Accordion.Header>
                <Accordion.Body>
                    <SafeHTMLDisplay htmlContent={course?.instructor_notes ?? ''} />
                </Accordion.Body>
            </Accordion.Item>

            {/* Both (instructor: website-only = Roster; onsite Enrollment Tool hidden) */}
            {showRosterOrEnrollment && (
                <Accordion.Item eventKey="2">
                    <Accordion.Header>{rosterHeader}</Accordion.Header>
                    <Accordion.Body>
                        <SessionRosterOrEnrollment
                            course={course}
                            featuredTlps={featuredTlps}
                            onWelcomeEmailClick={onWelcomeEmailClick}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            )}

            {/* Admin only: Waitlist / Waivers (hidden from instructors) */}
            {showWaitlistOrWaivers && (
                <Accordion.Item eventKey="3">
                    <Accordion.Header>{waitlistHeader}</Accordion.Header>
                    <Accordion.Body>
                        <SessionWaitlistOrWaivers
                            course={course}
                            featuredTlps={featuredTlps}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            )}

            {/* Both: Additional Expenses (New Expense button admin-only) */}
            {showExpenses && (
                <Accordion.Item eventKey="6">
                    <Accordion.Header>Additional Session Related Expenses</Accordion.Header>
                    <Accordion.Body>
                        <SimpleTable
                            columns={sessionExpensesColumns}
                            data={
                                course?.expenses?.map((expense) => ({
                                    ...expense,
                                    course,
                                    featured_tlps: featuredTlps
                                })) ?? []
                            }
                        />
                        {!isInstructor && (
                            <div className="d-flex justify-content-end mt-3">
                                <SimpleButton
                                    variant="primary"
                                    height={36}
                                    onClick={() => navigate(`/admin/finance/expenses/new?course_id=${course?.id}`)}
                                >
                                    New Expense
                                </SimpleButton>
                            </div>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            )}

            {/* Parent Emails — admin only (privacy). Sam: above Revisions. */}
            {showParentEmails && (
                <Accordion.Item eventKey="7">
                    <Accordion.Header>Parent Emails</Accordion.Header>
                    <Accordion.Body>
                        <SessionParentEmailsSection
                            rosterEmails={rosterEmails}
                            savedRosterEmails={course?.emails ?? ''}
                            waitlistEmails={waitlistEmails}
                            isUpdating={isUpdatingEmails}
                            isWebsitePayment={course?.payment_type === 'website'}
                            onRosterEmailChange={onRosterEmailChange}
                            onUpdate={onUpdateEmails}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            )}

            {/* Mobile-only: SFC Corporate Credit Card Receipts (Flat Rate only) — desktop in sidebar */}
            {course?.pay_type === 'flat_rate' && (
                <div className="d-lg-none">
                    <Accordion.Item eventKey="8">
                        <Accordion.Header>SFC Corporate Credit Card Receipts</Accordion.Header>
                        <Accordion.Body>
                            <SessionReceiptsSection course={course} idPrefix="mobile-" />
                        </Accordion.Body>
                    </Accordion.Item>
                </div>
            )}

            {/* Mobile-only: Contacts — admin only (privacy) */}
            {!isInstructor && (
                <div className="d-lg-none">
                    <Accordion.Item eventKey="11">
                        <Accordion.Header>Contacts</Accordion.Header>
                        <Accordion.Body>
                            <SessionContactsSection course={course} />
                        </Accordion.Body>
                    </Accordion.Item>
                </div>
            )}

            {/* Mobile-only: Invoices — admin only (financials) */}
            {!isInstructor && (
                <div className="d-lg-none">
                    <Accordion.Item eventKey="12">
                        <Accordion.Header>Invoices</Accordion.Header>
                        <Accordion.Body>
                            <SessionInvoicesSection course={course} />
                        </Accordion.Body>
                    </Accordion.Item>
                </div>
            )}

            {/* Mobile-only: Notes (admin only) — desktop in sidebar */}
            {currentUser?.isAdmin && (
                <div className="d-lg-none">
                    <Accordion.Item eventKey="13">
                        <Accordion.Header>Notes</Accordion.Header>
                        <Accordion.Body>
                            <SessionNotesSection course={course} />
                        </Accordion.Body>
                    </Accordion.Item>
                </div>
            )}

            {/* Mobile-only: Organization & Venue Info — admin only */}
            {!isInstructor && (
                <div className="d-lg-none">
                    <Accordion.Item eventKey="14">
                        <Accordion.Header>Organization &amp; Venue Info</Accordion.Header>
                        <Accordion.Body>
                            <Stack gap={3}>
                                <span className="text-secondary fw-semibold">{course?.venue?.phone}</span>
                                <span
                                    className="fw-semibold"
                                    style={{ color: 'rgba(94, 100, 105, .5)' }}
                                >
                  Organizations:
                </span>
                                {course?.venue?.organizations?.length ? (
                                    <Stack>
                                        {course.venue.organizations.map((org) => (
                                            <Link
                                                key={org.id}
                                                to={`/admin/places/organizations/${org.id}`}
                                                className="text-info fw-bold"
                                            >
                                                {org.name}
                                            </Link>
                                        ))}
                                    </Stack>
                                ) : (
                                    <span className="fst-italic">No organizations are assigned to this venue</span>
                                )}
                            </Stack>
                        </Accordion.Body>
                    </Accordion.Item>
                </div>
            )}

            {/* Mobile-only: Resources — bottom of mobile stack; desktop in sidebar */}
            <div className="d-lg-none">
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Resources</Accordion.Header>
                    <Accordion.Body>
                        <DetailSection>
                            <SessionResourcesSection
                                course={course}
                                onPDFClick={onPDFClick}
                                onSendTextReminder={onSendTextReminder}
                            />
                        </DetailSection>
                    </Accordion.Body>
                </Accordion.Item>
            </div>

            {/* Both (admin): Revisions — bottom, default closed */}
            {showRevisions && (
                <Accordion.Item eventKey="15">
                    <Accordion.Header>Revisions</Accordion.Header>
                    <Accordion.Body>
                        <SimpleTable
                            columns={revisionsColumns}
                            data={
                                course?.audits?.map((audit) => ({
                                    ...audit,
                                    course,
                                    featured_tlps: featuredTlps
                                })) ?? []
                            }
                        />
                    </Accordion.Body>
                </Accordion.Item>
            )}
        </SFCAccordion>
    );
};